import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { APP, ROUTES } from 'config';
import {
  ActionsMenu,
  Link,
  ListTotal,
  PageContentLayout,
  PageHeaderLayout,
  SideMenu,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import {
  benefitTemplateStatuses,
  COLOR_BY_STATUS,
} from 'store/benefit-templates';
import {
  adminBenefitTemplatesFiltersSchema,
  useAdminBenefitTemplates,
  useAdminCopyBenefitTemplate,
  useAdminDeleteBenefitTemplate,
} from 'store/admin-benefit-templates';
import { Divider, Stack } from '@mui/material';
import {
  DateRangeFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
} from 'components/filters';
import { useSearchParams } from 'utils/navigation';
import { benefitCategories } from 'store/benefits';
import moment from 'moment';
import { usePredefinedToasts } from 'utils/Toast';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useConfirm } from 'store/confirm';
import { useModalContext } from 'store/modal';
import { CreateBenefitTemplateModal } from './atoms';

export const AdminBenefitTemplatesList: FC = () => {
  const { t } = useTranslation();
  const { path } = useRouteMatch();
  const history = useHistory();
  const { openModal } = useModalContext();
  const { confirmation } = useConfirm();
  const predefinedToasts = usePredefinedToasts();

  const { searchParams: filters, navigate } = useSearchParams(
    adminBenefitTemplatesFiltersSchema,
  );

  const { data: templates, isLoading } = useAdminBenefitTemplates(filters);

  const { mutate: deleteTemplate, isLoading: isDeleting } =
    useAdminDeleteBenefitTemplate();
  const { mutate: copyTemplate, isLoading: isCopying } =
    useAdminCopyBenefitTemplate();

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('menu.templates'),
          },
        ]}
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            tabs={[
              {
                key: 'benefit-templates',
                label: t('menu.templates'),
                url: ROUTES.admin.benefitTemplates.list,
                isActive: path === ROUTES.admin.benefitTemplates.list,
              },
            ]}
          />
        }
      >
        <TableWrapper>
          <TableToolbar>
            <Stack gap={1}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                gap={1}
              >
                <ActionsMenu
                  type="spread"
                  items={[
                    {
                      label: t('benefitTemplates.newTemplate'),
                      color: 'primary',
                      onClick: () => {
                        openModal({
                          component: CreateBenefitTemplateModal,
                        });
                      },
                    },
                  ]}
                />
              </Stack>

              <Divider />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
              >
                <ListTotal total={templates?.total} />

                <Filters
                  value={filters}
                  initialValues={adminBenefitTemplatesFiltersSchema.parse({})}
                  onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                >
                  {({ value, initialValues, setFilter }) => (
                    <>
                      <FilterButton
                        label={t('benefits.benefit_type')}
                        value={value.category}
                        initialValue={initialValues.category}
                        onChange={(v) => setFilter('category', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <SelectFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            multiple
                            options={benefitCategories.map((category) => ({
                              key: category,
                              label: t(`menu.${category}`),
                            }))}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('benefitTemplates.modificationDate')}
                        value={value.date}
                        initialValue={initialValues.date}
                        onChange={(v) => setFilter('date', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <DateRangeFilter
                            value={value}
                            onApply={applyFilter}
                            onClear={clearFilter}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('common.status')}
                        value={value.status}
                        initialValue={initialValues.status}
                        onChange={(v) => setFilter('status', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <SelectFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            multiple
                            options={benefitTemplateStatuses.map((status) => ({
                              label: t(`status.${status}`),
                              key: status,
                            }))}
                            searchable={false}
                          />
                        )}
                      </FilterButton>

                      <OpenSearchFilter
                        value={value.search}
                        onApplyFilter={(v) => setFilter('search', v)}
                        placeholder={t('common.search')}
                      />
                    </>
                  )}
                </Filters>
              </Stack>
            </Stack>
          </TableToolbar>

          <Table
            data={templates?.items ?? []}
            isLoading={isLoading}
            columns={[
              {
                id: 'name',
                header: t('benefitTemplates.templateName'),
                cell: ({ row: { original: template } }) => {
                  return (
                    <Link
                      label={template.name}
                      to={ROUTES.admin.benefitTemplates.details.replace(
                        ':id',
                        template.id,
                      )}
                    />
                  );
                },
                meta: { sort: true },
              },
              {
                id: 'type',
                header: t('benefitTemplates.benefitCategory'),
                cell: ({ row: { original: template } }) => {
                  return t(`menu.${template.category}`);
                },
                meta: { sort: true },
              },
              {
                id: 'copiesMade',
                header: t('benefitTemplates.copiesMade'),
                cell: ({ row: { original: template } }) => {
                  return template.copiesMade;
                },
                meta: { sort: true, align: 'right' },
              },
              {
                id: 'modificationDate',
                header: t('benefitTemplates.modificationDate'),
                cell: ({ row: { original: template } }) => {
                  return moment(template.modifiedAt).format('l');
                },
                meta: { sort: true },
              },
              {
                id: 'status',
                header: t('common.status'),
                cell: ({ row: { original: template } }) => {
                  return (
                    <Typography color={COLOR_BY_STATUS[template.status]}>
                      {t(`status.${template.status}`)}
                    </Typography>
                  );
                },
                meta: { sort: true },
              },
              {
                id: 'actions',
                cell: ({ row: { original: template } }) => {
                  return (
                    <ActionsMenu
                      type="compact"
                      items={[
                        {
                          label: t('actions.make_copy'),
                          onClick: () => {
                            copyTemplate(
                              { benefitTemplateId: template.id },
                              {
                                onSuccess: ({ data }) => {
                                  predefinedToasts.success.added();
                                  history.push(
                                    ROUTES.admin.benefitTemplates.details.replace(
                                      ':id',
                                      data.id,
                                    ),
                                  );
                                },
                              },
                            );
                          },
                          disabled: isCopying,
                        },
                        {
                          label: t('actions.delete'),
                          onClick: async () => {
                            const { confirmed } = await confirmation(
                              t('common.areYouSureToDelete', {
                                title: template.name,
                              }),
                              undefined,
                              'danger',
                            );

                            if (confirmed) {
                              deleteTemplate(
                                { benefitTemplateId: template.id },
                                {
                                  onSuccess: () => {
                                    predefinedToasts.success.deleted();
                                  },
                                },
                              );
                            }
                          },
                          color: 'error',
                          disabled: isDeleting,
                        },
                      ]}
                    />
                  );
                },
              },
            ]}
            sort={{
              value: filters.sort,
              onChange: (v) => navigate({ params: { sort: v } }),
            }}
            pagination={
              templates && {
                count: templates.total,
                page: templates.page,
                pageSize: templates.pageSize,
                pageSizeOptions: APP.pagination.defaults.pageSize,
                onChange: (page, pageSize) => {
                  navigate({ params: { page, pageSize } });
                },
              }
            }
          />
        </TableWrapper>
      </PageContentLayout>
    </Stack>
  );
};
